import { useContext, useEffect, useRef } from "react";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import mapboxgl from "mapbox-gl";

import { MapContext } from "fond/map/MapProvider";
import { useStyleEpoch } from "fond/map/useStyleEpoch";

import { useGeoReferencer } from "./useGeoReferencer";

import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";

const SOURCE_ID = "georef-gcp-source";
const CIRCLE_LAYER_ID = "georef-gcp-circles";
const LABEL_LAYER_ID = "georef-gcp-labels";

/**
 * Manages map click handling for placing GCP map points,
 * and renders GCP markers on the map as a GeoJSON source + circle/symbol layers.
 */
const MapGCPHandler: React.FC = () => {
  const { map } = useContext(MapContext);
  const { gcps, activeGcpId, mode, setMapPoint } = useGeoReferencer();
  const prevCursorRef = useRef<string>("");
  const styleEpoch = useStyleEpoch(map);

  // Add geocoder (search) control
  useEffect(() => {
    if (!map) return;

    const GeocoderControl = MapboxGeocoder as any;
    const geocoder = new GeocoderControl({
      accessToken: `${import.meta.env.REACT_APP_MAPBOX_KEY}`,
      mapboxgl,
      marker: true,
      collapsed: true,
      placeholder: "Search location...",
    });
    map.addControl(geocoder, "top-right");

    // eslint-disable-next-line consistent-return
    return () => {
      map.removeControl(geocoder);
    };
  }, [map]);

  // Handle map click for placing GCP map coordinates
  useEffect(() => {
    if (!map || mode !== "place-map" || !activeGcpId) return;

    prevCursorRef.current = map.getCanvas().style.cursor;
    map.getCanvas().style.cursor = "crosshair";

    const handler = (e: mapboxgl.MapMouseEvent) => {
      setMapPoint(activeGcpId, e.lngLat.lng, e.lngLat.lat);
    };

    map.on("click", handler);

    return () => {
      map.off("click", handler);
      map.getCanvas().style.cursor = prevCursorRef.current;
    };
  }, [map, mode, activeGcpId, setMapPoint]);

  // Manage GeoJSON source + layers for GCP markers on the map
  useEffect(() => {
    if (!map) return;

    const geojsonData: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: gcps
        .filter((gcp) => gcp.mapLng != null && gcp.mapLat != null)
        .map((gcp) => ({
          type: "Feature" as const,
          geometry: {
            type: "Point" as const,
            coordinates: [gcp.mapLng!, gcp.mapLat!],
          },
          properties: {
            label: gcp.label.replace("GCP ", ""),
            isActive: gcp.id === activeGcpId,
            isComplete: gcp.imageX !== 0 || gcp.imageY !== 0,
          },
        })),
    };

    const source = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (source) {
      source.setData(geojsonData);
    } else {
      map.addSource(SOURCE_ID, { type: "geojson", data: geojsonData });

      map.addLayer({
        id: CIRCLE_LAYER_ID,
        type: "circle",
        source: SOURCE_ID,
        paint: {
          "circle-radius": 8,
          "circle-color": ["case", ["get", "isActive"], "#FFC107", ["get", "isComplete"], "#4CAF50", "#F44336"],
          "circle-stroke-color": "white",
          "circle-stroke-width": 2,
        },
      });

      map.addLayer({
        id: LABEL_LAYER_ID,
        type: "symbol",
        source: SOURCE_ID,
        layout: {
          "text-field": ["get", "label"],
          "text-size": 11,
          "text-font": ["Open Sans Bold"],
          "text-allow-overlap": true,
          "icon-allow-overlap": true,
        },
        paint: {
          "text-color": "white",
        },
      });
    }
  }, [map, gcps, activeGcpId, styleEpoch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (!map) return;
      if (map.getLayer(LABEL_LAYER_ID)) map.removeLayer(LABEL_LAYER_ID);
      if (map.getLayer(CIRCLE_LAYER_ID)) map.removeLayer(CIRCLE_LAYER_ID);
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    };
  }, [map]);

  return null;
};

export default MapGCPHandler;
